/**
 * 买房、售出、估价、租房控制面板
 * 
 * @Author: Focci
 * @Date: 2023-07-13 11:38:18
 * @Last Modified by: Focci
 * @Last Modified time: 2023-07-13 11:38:18
 */

'use client'

import PropTypes from 'prop-types'
import clsx from 'clsx'
import CheckboxIcon from '@img/checkbox-icon.svg'
import SVGMoredot from '@img/moredot.svg'
import useApp from '@hook/useApp'
import useStorage from '@hook/useStorage'
import { houseSearchType } from '@lib/config'
import { useMapState } from '@comp/mapbox'
import { useTranslations } from 'next-intl'
import { ButtonBase, Checkbox } from '@comp/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { map } from 'lodash-es'
// import { getSetter } from '@lib/utils'
// import ControllerWrapper from './ControllerWrapper'

function ExpandTrigger(props) {
  return (
    <ButtonBase className="rounded-md" {...props}>
      <div className="rounded-md bg-c3b flex items-center justify-center h-8 w-8">
        <SVGMoredot />
      </div>
    </ButtonBase>
  )
}

/**
 * 地图marker与房源类型的名称映射
 * 对应 allOpts 的 key
 */
export const markerMap = {
  [houseSearchType.residential]: 'property',
  [houseSearchType.newHomes]: 'property',
  [houseSearchType.development]: 'estimate',
  [houseSearchType.sold]: 'sold',
  [houseSearchType.estimate]: 'estimate',
  [houseSearchType.rental]: 'rental',
  [houseSearchType.rural]: 'rural',
  [houseSearchType.commercialForSale]: 'commercialForSale',
  [houseSearchType.commercialForLease]: 'commercialForLease',
}

/**
 * 获取所有的房源标记选项
 * @param {string} type 房源类型
 */
export function useMarkerCtrlOptions(type) {
  const tm = useTranslations('menu')
  const ts = useTranslations('search')
  
  const allOpts = useMemo(() => ({
    property: { label: ts('listingForSale'), value: 'property', color: 'primary' },
    sold: { label: ts('recentlySold'), value: 'sold', color: 'error' },
    estimate: { label: tm('estimate'), value: 'estimate', color: 'success' },
    rental: { label: ts('rental'), value: 'rental', color: 'warning' },
    rural: { label: tm('rural'), value: 'rural', color: 'darkGray' },
    commercialForSale: { label: tm('commercialForSale'), value: 'commercialForSale', color: 'deepBlue' },
    commercialForLease: { label: tm('commercialForLease'), value: 'commercialForLease', color: 'coffee' },
  }), [tm, ts])

  return useMemo(() => {
    let types = []
    if([
      houseSearchType.commercialForSale,
      houseSearchType.rural,
      houseSearchType.commercialForLease,
    ].indexOf(type) > -1) {
      types = (['rural', 'commercialForSale', 'commercialForLease'])
    } else if(type === houseSearchType.rent) {
      types = (['rental'])
    } else {
      types = ['property', 'sold', 'estimate', 'rental']
    }

    return map(types, (t) => allOpts[t])
  }, [allOpts, type])
}

export default function MarkerController({ type = '' }) {
  const ta = useTranslations('article')
  const mapboxState = useMapState()
  const options = useMarkerCtrlOptions(type)

  const { isMobile } = useApp()
  const [expand, setExpand] = useState(false) // 面板是否展开
  const { getItem, setItem } = useStorage('HG_MAPBOX_MARKERS_PANEL_EXPAND', true)

  const handleTrigerClick = useCallback(() => {
    setItem(true)
    setExpand((prev) => !prev)
  }, [setItem])

  const handleChange = useCallback((e, { value }) => {
    mapboxState.valueSetter(value, e.target.checked)
  }, [mapboxState])

  const handleExpand = useCallback(() => {
    setItem(false)
    setExpand((prev) => !prev)
  }, [setItem])

  // 判断面板展开与收起
  useEffect(() => {
    const isExpand = getItem()
    const val = isExpand !== null ? isExpand : !isMobile
    
    setExpand(val)
  }, [isMobile, getItem])

  if(!expand) {
    return (
      <ExpandTrigger onClick={handleTrigerClick} />
    )
  }
  
  return (
    <div 
      className="
        bg-c3b rounded-md text-white shadow-large 
        animate-fade-in origin-top-left
      "
    >
      {options.map((opt, index) => (
        <label
          key={opt.value} 
          className={clsx([
            'flex items-center justify-between p-2 cursor-pointer',
            index !== 0 && 'border-t border-t-1 border-t-solid border-t-white/40'
          ])}
        >
          <span className="pr-4 text-f.7">{opt.label}</span>
          <span className="flex relative">
            {mapboxState[opt.value] && (
              <span className="absolute inset-[3px] bg-white z-0" />
            )}
            <Checkbox
              className="relative z-[1]"
              checked={mapboxState[opt.value]}
              onChange={(e) => handleChange(e, opt)}
              color={opt.color}
              icon={<CheckboxIcon />}
              sx={{ color: 'white' }} 
            />
          </span>
        </label>
      ))}
      <ButtonBase 
        className="ounded-b-md w-full"
        onClick={handleExpand}
      >
        <div 
          className="
            flex items-center justify-between w-full px-2 py-1 
            bg-[#5d656e] rounded-b-md
          "
        >
          <span className="underline text-f.7">{ta('hide')}</span>
          <SVGMoredot width=".6rem" />
        </div>
      </ButtonBase>
    </div>
  )
}

MarkerController.propTypes = {
  type: PropTypes.string,
}
